import { FC, useEffect } from 'react';
import { Button, Radio } from 'components/core';
import { Styled, OrderPaymentProps } from './';
import { useDoctorProfile, usePayments } from 'reactQuery/queries';
import { getIconByNotificationType } from 'components/core/Notification';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';
import {
  getCreditCardLogo,
  isPayByInvoiceNotEligible,
  isPaymentOptionAvailable,
  reorderCreditCards,
} from './OrderPayment.helper';
import Image from 'next/legacy/image';
import getConfig from 'next/config';
import { CreditCardClientData } from 'types';
import { useI18n } from 'contexts/i18n';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { useRetriveCart } from 'reactQuery/queries';
import getButtonLabel from '../OrderCheckout/CurrentStep';
import { useReadOnlyUser } from 'utils/hooks';

const OrderPayment: FC<OrderPaymentProps> = ({
  mode,
  onSubmit,
  selectedCreditCard,
  setCreditCard,
  orderFailure = '',
  setOrderFailure,
  isPayByInvoiceSelected,
  setIsPayByInvoiceSelected,
  isCartHavingSubscriptionProducts,
}) => {
  const { publicRuntimeConfig } = getConfig();
  const i18n = useI18n();
  const {
    data: paymentMethods,
    isLoading: isCreditCardLoading,
    isFetching: isCreditCardFetching,
    isIdle,
    isError,
  } = usePayments();

  const { data: doctorProfile } = useDoctorProfile();
  const { data: currentCart } = useRetriveCart();
  const { isReadOnlyUser } = useReadOnlyUser();

  const title = getButtonLabel(currentCart?.checkoutStates, 'payment');

  useEffect(() => {
    if (
      paymentMethods &&
      paymentMethods.creditCards &&
      paymentMethods.creditCards?.length > 0
    ) {
      const defaultCreditCard = paymentMethods.creditCards.filter(
        card => card.default
      )[0];
      if (defaultCreditCard) {
        setCreditCard(defaultCreditCard);
      } else {
        setCreditCard(paymentMethods.creditCards[0]);
      }
      setIsPayByInvoiceSelected(false);
    } else {
      if (paymentMethods && paymentMethods.isPayByInvoiceEnabled) {
        setIsPayByInvoiceSelected(true);
        setCreditCard(undefined);
      }
    }
  }, [paymentMethods, setCreditCard, setIsPayByInvoiceSelected]);

  useEffect(() => {
    if (
      paymentMethods &&
      paymentMethods.creditCards &&
      paymentMethods.creditCards?.length > 0
    ) {
      const defaultCreditCard = paymentMethods.creditCards.filter(
        card => card.default
      )[0];
      if (defaultCreditCard) {
        setCreditCard(defaultCreditCard);
      } else {
        setCreditCard(paymentMethods.creditCards[0]);
      }
    }
  }, [paymentMethods?.creditCards]);

  if (isCreditCardLoading || isIdle || isCreditCardFetching) {
    return (
      <LoadingWrapper>
        <LoadingSpinner width="25px" height="25px" isPrimary={true} />
      </LoadingWrapper>
    );
  }

  const handleCreditCardChange = (creditCard: CreditCardClientData) => {
    setOrderFailure('');
    setCreditCard(creditCard);
    setIsPayByInvoiceSelected(false);
  };

  const handlePayByInvoice = () => {
    setIsPayByInvoiceSelected(true);
    setCreditCard(undefined);
  };

  if (isError) {
    return (
      <>{i18n['Something went wrong while fetching credit card detail.']}</>
    );
  }

  return (
    <>
      {isCartHavingSubscriptionProducts && (
        <Styled.Notification>
          <Styled.NotificationIcon>
            {getIconByNotificationType('info')}
          </Styled.NotificationIcon>
          <Styled.NotificationBody>
            {
              i18n[
                'Your payment method shall be applicable towards your future subscription payments.'
              ]
            }
          </Styled.NotificationBody>
        </Styled.Notification>
      )}
      {mode === 'create' && (
        <>
          {paymentMethods.isPayByInvoiceEnabled && (
            <Styled.PayByInvoiceContainer
              isSelected={isPayByInvoiceSelected}
              onClick={handlePayByInvoice}
            >
              <Radio
                checked={isPayByInvoiceSelected}
                onChange={handlePayByInvoice}
              />
              <Image
                src={getCreditCardLogo('invoice')}
                alt="logo"
                width={40}
                height={40}
              />
              <Styled.PayByInvoiceTitle>
                {i18n['Pay By Invoice']}
              </Styled.PayByInvoiceTitle>
            </Styled.PayByInvoiceContainer>
          )}
          {isPayByInvoiceNotEligible(doctorProfile?.country)
            ? reorderCreditCards(paymentMethods.creditCards)?.map(
                creditCard => {
                  const isCreditCardAuthorizationFailure =
                    orderFailure === 'CREDIT_CARD_ERROR' &&
                    creditCard.id === selectedCreditCard?.id;
                  return (
                    <>
                      <Styled.CreditCardContainer
                        key={creditCard.id}
                        isSelected={selectedCreditCard?.id === creditCard.id}
                        onClick={() => handleCreditCardChange(creditCard)}
                        isCreditCardAuthorizationFailure={
                          isCreditCardAuthorizationFailure
                        }
                      >
                        <Styled.CreditCard>
                          <Radio
                            checked={creditCard.id === selectedCreditCard?.id}
                            onChange={() => handleCreditCardChange(creditCard)}
                          />
                          <Image
                            src={getCreditCardLogo(creditCard.cardType)}
                            alt="logo"
                            width={40}
                            height={40}
                          />
                          <Styled.CardHolderName>
                            {creditCard.name}
                          </Styled.CardHolderName>
                          <Styled.LastDigits>
                            {`${i18n['ending in']} ${creditCard.last4Digit}`}{' '}
                          </Styled.LastDigits>
                          <Styled.Expiry>{`${i18n['Exp:']} ${creditCard.expiry}`}</Styled.Expiry>
                        </Styled.CreditCard>
                        {creditCard.default && (
                          <Styled.DefaultCard>
                            {i18n['Default']}
                          </Styled.DefaultCard>
                        )}
                        {isCreditCardAuthorizationFailure && (
                          <Styled.CreditCardErrorMessage>
                            {i18n['Payment verification failed.']}
                          </Styled.CreditCardErrorMessage>
                        )}
                      </Styled.CreditCardContainer>
                    </>
                  );
                }
              )
            : null}
          {!isPaymentOptionAvailable(paymentMethods) && (
            <Styled.AddButtonWrapper>
              <Button
                onClick={() => {
                  window.open(publicRuntimeConfig.PAYMENT_APP_URL, '_blank');
                }}
                disabled={isReadOnlyUser}
              >
                <AiOutlinePlusCircle />
                <Styled.ButtonLabel>
                  {i18n['Add Payment Method']}
                </Styled.ButtonLabel>
              </Button>
            </Styled.AddButtonWrapper>
          )}
          <Styled.ButtonWrapper>
            {isPaymentOptionAvailable(paymentMethods) && (
              <>
                {isPayByInvoiceNotEligible(doctorProfile?.country) ? (
                  <Button
                    onClick={() => {
                      window.open(
                        publicRuntimeConfig.PAYMENT_APP_URL,
                        '_blank'
                      );
                    }}
                    variant="secondary"
                    disabled={isReadOnlyUser}
                  >
                    <AiOutlinePlusCircle />

                    <Styled.ButtonLabel>
                      {i18n['Add Payment Method']}
                    </Styled.ButtonLabel>
                  </Button>
                ) : null}
                <Styled.ContinuetoReviewButton>
                  <Button size="md" onClick={onSubmit}>
                    {i18n['Continue to Order Review']}
                  </Button>
                </Styled.ContinuetoReviewButton>
              </>
            )}
          </Styled.ButtonWrapper>
        </>
      )}

      {mode === 'edit' && (
        <>
          {selectedCreditCard?.id && (
            <Styled.CreditCardReadOnly>
              <Image
                src={getCreditCardLogo(selectedCreditCard.cardType)}
                alt="logo"
                width={40}
                height={40}
              />
              <Styled.CardHolderName>
                {selectedCreditCard.name}
              </Styled.CardHolderName>
              <Styled.LastDigits>
                {`${i18n['ending in']} ${selectedCreditCard.last4Digit}`}{' '}
              </Styled.LastDigits>

              <Styled.Expiry>{`${i18n['Exp:']} ${selectedCreditCard.expiry}`}</Styled.Expiry>
            </Styled.CreditCardReadOnly>
          )}

          {paymentMethods.isPayByInvoiceEnabled && isPayByInvoiceSelected && (
            <Styled.CreditCardReadOnly>
              <Image
                src={getCreditCardLogo('invoice')}
                alt="logo"
                width={40}
                height={40}
              />
              <Styled.PayByInvoiceTitle>
                {i18n['Pay By Invoice']}
              </Styled.PayByInvoiceTitle>
            </Styled.CreditCardReadOnly>
          )}
        </>
      )}
    </>
  );
};

export default OrderPayment;
